import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  InputBase,
  Typography,
  CircularProgress,
  IconButton,
  ClickAwayListener,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search } from '@mui/icons-material';

import Store from 'js/containers/Search/Store';
import Establishment from 'js/containers/Search/Establishment';
import { getProducts } from 'js/library/utils/API/getProducts';
import { getPromotions } from 'js/library/utils/API/getPromotions';
import { getEstablishment } from 'js/library/utils/API/getEstablishment';
import { getLastUserInfo, trackEventMatomo } from 'js/library/utils/helpers';

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    color: 'black',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

export const SearchBar = () => {
  const userInfo = getLastUserInfo();
  const [storeList, setStoreList] = React.useState(false);
  const [establishmentList, setEstablishmentList] = React.useState(false);
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [tootipIsOpen, setTooltipIsOpen] = React.useState(false);
  const [label, setlabel] = React.useState('');
  const { t } = useTranslation();
  let timeDebounce = null;

  async function getData(event) {
    event.preventDefault();
    setEstablishmentList(null);
    setStoreList(null);
    setSearchOpen(true);

    try {
      const payload = {
        userQuery: label,
        productType: ['digital', 'physical'],
        orderType: 'relevance',
        plataform: 'web',
        from: 0,
        size: 50,
      };

      const payloadEstab = {
        ...payload,
        hasPromo: false,
        establishmentType: ['digital', 'physical'],
      };
      delete payloadEstab.productType;
      delete payloadEstab.hasVoucher;

      const [promotions, products, establishments] = await Promise.allSettled([
        getPromotions(userInfo.uId, payload),
        getProducts(userInfo.uId, payload),
        getEstablishment(userInfo.uId, payloadEstab),
      ]);

      let list = [];

      if (promotions.status === 'fulfilled') list = [...promotions.value];
      if (products.status === 'fulfilled') list = [...list, ...products.value];
      if (establishments.status === 'fulfilled') setEstablishmentList(establishments.value);

      setStoreList(list);
    } catch (error) {
      console.log(error);
      setStoreList('error');
      setEstablishmentList('error');
    }
  }

  function closeSearchBar() {
    setSearchOpen(false);
  }

  function scrollMatomo() {
    clearTimeout(timeDebounce);
    timeDebounce = setTimeout(() => {
      trackEventMatomo('Header', 'scroll', 'lista', 'Caixa de pesquisa');
    }, 400);
  }

  return (
    <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
      <Tooltip arrow open={tootipIsOpen} title={t('errors.mandatory_three')}>
        <Box className="search-width">
          <Box
            sx={{
              position: 'relative',
              borderRadius: '27px',
              backgroundColor: '#fff',
              width: '500px',
              maxWidth: '100%',
              '@media (max-width: 900px)': {
                width: '100%',
              },
            }}
          >
            <form
              onSubmit={(e) => {
                if (label.length > 2) {
                  setTooltipIsOpen(false);
                  getData(e);
                } else {
                  e.preventDefault();
                  setTooltipIsOpen(true);
                  setTimeout(() => setTooltipIsOpen(false), 2000);
                }
              }}
            >
              <Box
                sx={{
                  padding: (theme) => theme.spacing(0, 2),
                  height: '100%',
                  position: 'absolute',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#BABABA',
                }}
              >
                <IconButton type="submit">
                  <Search />
                </IconButton>
              </Box>

              <StyledInputBase
                id="searchLabel"
                value={label}
                onChange={(e) => setlabel(e.target.value)}
                placeholder={t('nav_bar.search')}
                inputProps={{ 'aria-label': t('nav_bar.search') }}
              />
            </form>
          </Box>

          {(establishmentList === false && storeList === false) || !searchOpen ? null : (
            <Box className="search-box-container">
              <Box onScroll={scrollMatomo} className="search-box-scroll">
                {storeList === null && establishmentList === null ? (
                  <Box className="search-box" sx={{ textAlign: 'center' }}>
                    <CircularProgress sx={{ padding: 3, boxSizing: 'content-box' }} />
                  </Box>
                ) : storeList === 'error' && establishmentList === 'error' ? (
                  <Box className="search-box" sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1">
                      Ocorreu um erro! Por favor tente novamente.
                    </Typography>
                  </Box>
                ) : establishmentList.length === 0 && storeList === 0 ? (
                  <Box className="search-box" sx={{ padding: 1, textAlign: 'center' }}>
                    {t('errors.no_data_found')}
                  </Box>
                ) : (
                  <Box>
                    {storeList.map((row, i) => (
                      <Store key={i} func={closeSearchBar} data={row} />
                    ))}
                    {establishmentList.map((row, i) => (
                      <Establishment key={i} func={closeSearchBar} data={row} />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default SearchBar;
